import { Input, Info } from '@/shared/ui';
import { FormikErrors, FormikTouched } from 'formik';
import { AddressValidation, ExtendedAddress } from '@/shared/components/address-form/types';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { acceptNumeric } from '@/shared/helpers/input';
import { ChangeEvent, useCallback } from 'react';

interface ManualAddressProps {
    handleChange: (fieldname: string, value: string) => void;
    values: ExtendedAddress;
    errors: FormikErrors<ExtendedAddress>;
    touched: FormikTouched<ExtendedAddress>;
    selectedAddressState?: AddressValidation;
    error?: string;
    formTitle?: string;
    formTitleClassName?: string;
    showApartmentSuiteBoxNumberField?: boolean;
    editedAddress?: boolean;
}

export const ManualAddressFormElements: React.FC<ManualAddressProps> = ({
    handleChange,
    values,
    errors,
    touched,
    selectedAddressState,
    error = '',
    formTitle = 'Shipping Address',
    formTitleClassName = '',
    showApartmentSuiteBoxNumberField,
    editedAddress
}) => {
    const handleNumericChange = useCallback((field: string, handleChange: (field: string, value: string) => void) => {
        return (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            acceptNumeric(() => {
                handleChange(field, event.target.value);
            })(event);
        };
    }, []);

    return (
        <>
            {formTitle && <span data-testid="manual-address-form-elements-title" className={`${formTitleClassName} w-full text-2xl md:text-3xl mb-4`}>{formTitle}</span>}
            {error && <span data-testid="manual-address-form-elements-error" className="mb-4">{selectedAddressState?.error}</span>}
            <Input
                className="manual_address__form_elements__address"
                label="Street Address"
                name="addressLine1"
                type="text"
                placeholder="ex. 28520 Wood Canyon Dr."
                value={values.addressLine1}
                error={touched.addressLine1 && Boolean(errors.addressLine1)}
                errorMessage={errors.addressLine1}
                onChange={event => handleChange('addressLine1', event.target.value)}
                required
                testId="manual-address-address-line-1"
            />
            {selectedAddressState?.needsUnit && (
                <Info testId="manual-address-elements-needs-unit-info" className="my-4">
                    <p className="font-dmSans text-sm sm:text-15 leading-[1.43rem] text-primary-900">
                        The address you entered was found but more information is needed{' '}
                        <span className="font-dmSansMedium font-bold text-sm sm:text-base">(such as an apartment, suite, or box number)</span> to match to a specific address.
                        Please enter this information below.
                    </p>
                </Info>
            )}
            {(showApartmentSuiteBoxNumberField || values.unit || editedAddress) && (
                <Input
                    className="manual_address__form_elements__unit"
                    label="Apartment/Suite/Box Number"
                    name="unit"
                    type="text"
                    placeholder="ex. #123, Suite 303"
                    value={values.unit}
                    error={touched?.unit && Boolean(errors?.unit)}
                    errorMessage={errors?.unit}
                    onChange={event => handleChange('unit', event.target.value)}
                    testId="manual-address-unit"
                />
            )}
            <Input
                className="manual_address__form_elements__city"
                label="City"
                name="city"
                type="text"
                placeholder="ex. Aliso Viejo"
                error={touched.city && Boolean(errors.city)}
                errorMessage={errors.city}
                value={values.city}
                onChange={event => handleChange('city', event.target.value)}
                required
                testId="manual-address-city"
            />
            <div className="flex flex-wrap gap-4">
                <Input
                    label="State"
                    name="state"
                    type="text"
                    placeholder="ex. CA"
                    error={touched.state && Boolean(errors.state)}
                    errorMessage={errors.state}
                    value={values.state}
                    onChange={event => handleChange('state', event.target.value)}
                    maxLength={2}
                    required
                    className="manual_address__form_elements__state min-w-[10rem] flex-1"
                    testId="manual-address-state"
                />
                <Input
                    label="Zip"
                    name="zip"
                    type="text"
                    placeholder="ex. 92656"
                    error={touched.zip && Boolean(errors.zip)}
                    errorMessage={errors.zip}
                    value={values.zip}
                    onChange={handleNumericChange('zip', handleChange)}
                    required
                    maxLength={5}
                    className="manual_address__form_elements__zip min-w-[10rem] flex-1"
                    testId="manual-address-zip"
                />
            </div>
        </>
    );
};

export default withScrollRestoration(ManualAddressFormElements);
